import { PerguntaParametrizadaResponse } from './models/pergunta.models';

/** Plataformas que não devem aparecer como chip na nuvem. */
const EXCLUDED_PLATFORMS = new Set(['datastories', 'DataStories']);

/**
 * Converte `parametros` da listagem (objeto flat da API) em query params
 * para a URL de detalhe — mesmo padrão do infogov-front-17.
 */
export function toQueryParams(
  parametros: unknown,
): Record<string, string> {
  if (!parametros || typeof parametros !== 'object') return {};
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(
    parametros as Record<string, unknown>,
  )) {
    if (value == null || typeof value === 'object') continue;
    result[key] = String(value);
  }
  return result;
}

/** Ordem de preferência para exibição dos chips de plataforma. */
const PLATFORM_ORDER = [
  'Igualdade Racial',
  'Racial Equality',
  'Pessoal',
  'Personnel',
  'Compras',
  'Procurement',
  'Transferências',
  'Transfers',
];

export interface PlatformChip {
  name: string;
  count: number;
}

/**
 * Título curto do card: usa o `nomeCurto` curado do backend quando disponível,
 * e cai no derivado do `texto` como fallback.
 */
export function shortTitle(pergunta: PerguntaParametrizadaResponse): string {
  const curado = pergunta.nomeCurto?.trim();
  return curado ? curado : deriveShortTitle(pergunta.texto);
}

/**
 * Deriva um título curto para o card a partir do texto da pergunta.
 * Mantém a pergunta legível (normaliza espaços, remove ponto final) e apenas
 * trunca quando excessivamente longa — usado como fallback.
 */
export function deriveShortTitle(texto: string): string {
  const clean = (texto ?? '').replace(/\s+/g, ' ').trim();
  if (clean.length <= 90) return clean;
  const truncated = clean.slice(0, 90);
  const lastSpace = truncated.lastIndexOf(' ');
  return `${truncated.slice(0, lastSpace > 40 ? lastSpace : 90).trim()}…`;
}

/** Extrai as plataformas (a partir de `pergunta.sistema`) com contagem. */
export function derivePlatforms(
  perguntas: PerguntaParametrizadaResponse[],
): PlatformChip[] {
  const counts = new Map<string, number>();
  for (const p of perguntas) {
    const sistema = p.pergunta?.sistema?.trim();
    if (!sistema || EXCLUDED_PLATFORMS.has(sistema)) continue;
    counts.set(sistema, (counts.get(sistema) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => {
      const ia = PLATFORM_ORDER.indexOf(a.name);
      const ib = PLATFORM_ORDER.indexOf(b.name);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return a.name.localeCompare(b.name, 'pt-BR');
    });
}

/** Perguntas visíveis na nuvem (exclui plataformas não listadas, ex. datastories). */
export function visibleQuestions(
  perguntas: PerguntaParametrizadaResponse[],
): PerguntaParametrizadaResponse[] {
  return perguntas.filter(
    (p) =>
      p.pergunta?.sistema && !EXCLUDED_PLATFORMS.has(p.pergunta.sistema.trim()),
  );
}
